<template>
  <div class="subscription-info">

    <!-- Status banner -->
    <div class="status-banner warning" v-if="subscription.cancelAtPeriodEnd">
      <i class="icofont-warning"></i>
      Subscription cancels on <b>{{ formatDate(subscription.currentPeriodEnd) }}</b>.
      <button class="link-btn" @click="reactivate" :disabled="reactivateLoading">
        {{ reactivateLoading ? 'Loading…' : 'Undo cancellation' }}
      </button>
    </div>
    <div class="status-banner error" v-else-if="subscription.status === 'PAST_DUE'">
      <i class="icofont-warning"></i>
      Payment failed — please update your payment method.
      <button class="link-btn" @click="openPortal">Update payment</button>
    </div>

    <!-- Billing interval toggle -->
    <div class="billing-toggle">
      <span :class="{ active: interval === 'monthly' }" @click="interval = 'monthly'">Monthly</span>
      <div class="toggle-track" @click="interval = interval === 'monthly' ? 'yearly' : 'monthly'">
        <div class="toggle-thumb" :class="{ right: interval === 'yearly' }"></div>
      </div>
      <span :class="{ active: interval === 'yearly' }" @click="interval = 'yearly'">
        Yearly
        <span class="save-badge">Save 2 months</span>
      </span>
    </div>

    <!-- Product cards -->
    <div class="products-grid">

      <div
        v-for="product in sortedProducts"
        :key="product.id"
        class="product-card"
        :class="{
          'is-active': isActiveProduct(product),
          'is-popular': product.isPopular
        }"
      >
        <div class="popular-badge" v-if="product.isPopular && !isActiveProduct(product)">Most popular</div>
        <div class="active-badge" v-if="isActiveProduct(product)">Current plan</div>
        <h3 class="product-name">{{ product.name }}</h3>
        <div class="product-price" v-if="product.isFree">
          <span class="amount">0</span>
          <span class="currency">NOK</span>
        </div>
        <div class="product-price" v-else-if="getAmount(product) != null">
          <span class="amount">{{ formatAmount(getAmount(product)) }}</span>
          <span class="currency">NOK</span>
          <span class="period">/ {{ interval === 'monthly' ? 'mo' : 'yr' }}</span>
        </div>
        <div class="product-price unavailable" v-else>—</div>
        <p class="product-description">{{ product.description }}</p>
        <ul class="feature-list">
          <li v-for="(feature, i) in product.features" :key="i">{{ feature }}</li>
        </ul>
        <div class="card-footer">
          <div class="subscription-meta" v-if="isActiveProduct(product) && !product.isFree">
            <span v-if="subscription.cancelAtPeriodEnd">Ends {{ formatDate(subscription.currentPeriodEnd) }}</span>
            <span v-else-if="subscription.currentPeriodEnd">Renews {{ formatDate(subscription.currentPeriodEnd) }}</span>
          </div>
          <template v-if="product.isFree">
            <div class="current-plan-label" v-if="isActiveProduct(product)">You're on this plan</div>
            <template v-else>
              <div class="cancel-confirm" v-if="confirming === 'downgrade'">
                <p>Cancel your subscription? You'll keep access until {{ formatDate(subscription.currentPeriodEnd) }}.</p>
                <div class="confirm-actions">
                  <button class="btn-danger-sm" @click="confirmCancel" :disabled="cancelLoading">
                    {{ cancelLoading ? 'Cancelling…' : 'Yes, cancel' }}
                  </button>
                  <button class="btn-ghost-sm" @click="confirming = null">Keep plan</button>
                </div>
              </div>
              <button v-else class="btn-secondary" @click="confirming = 'downgrade'" :disabled="cancelLoading">
                Downgrade
              </button>
            </template>
          </template>
          <template v-else-if="isActiveProduct(product)">
            <div class="cancel-confirm" v-if="confirming === product.id">
              <p>Cancel your subscription? You'll keep access until {{ formatDate(subscription.currentPeriodEnd) }}.</p>
              <div class="confirm-actions">
                <button class="btn-danger-sm" @click="confirmCancel" :disabled="cancelLoading">
                  {{ cancelLoading ? 'Cancelling…' : 'Yes, cancel' }}
                </button>
                <button class="btn-ghost-sm" @click="confirming = null">Keep plan</button>
              </div>
            </div>
            <template v-else-if="subscription.cancelAtPeriodEnd">
              <button class="btn-secondary" @click="reactivate" :disabled="reactivateLoading">
                {{ reactivateLoading ? 'Loading…' : 'Reactivate' }}
              </button>
            </template>
            <template v-else>
              <button class="btn-secondary" @click="confirming = product.id" :disabled="cancelLoading">
                Cancel subscription
              </button>
            </template>
          </template>
          <button
            v-else-if="getAmount(product) != null"
            class="btn-primary"
            :class="{ 'btn-popular': product.isPopular }"
            @click="startCheckout(product)"
            :disabled="checkoutLoading === product.id"
          >
            <span v-if="checkoutLoading === product.id">Loading…</span>
            <span v-else>Get {{ product.name }}</span>
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import moment from "moment"

export default {
  name: "SubscriptionInfo",
  data: () => ({
    interval: "monthly",
    checkoutLoading: null,
    portalLoading: false,
    cancelLoading: false,
    reactivateLoading: false,
    confirming: null,
  }),
  computed: {
    subscription() {
      return this.$store.getters.subscription || {}
    },
    products() {
      return this.$store.getters.products || []
    },
    freeProduct() {
      return this.products.find(p => p.isFree) ?? { isFree: true, name: 'Free', features: [], sortOrder: -1 }
    },
    sortedProducts() {
      const paid = this.products.filter(p => !p.isFree).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      return [this.freeProduct, ...paid]
    },
    isFree() {
      return this.$store.getters.subscriptionProduct?.isFree ?? true
    },
  },
  methods: {
    isActiveProduct(product) {
      if (product.isFree) return this.isFree
      return (
        this.subscription.subscriptionProductId === product.id &&
        ["ACTIVE", "TRIALING", "PAST_DUE"].includes(this.subscription.status)
      )
    },
    getAmount(product) {
      return this.interval === "yearly" ? product.yearlyAmount : product.monthlyAmount
    },
    formatAmount(amount) {
      return Math.round(amount / 100).toLocaleString("nb-NO")
    },
    formatDate(date) {
      return date ? moment(date).format("D MMM YYYY") : ""
    },
    navigate(url) {
      if (!url) return
      location.href = url
    },
    async startCheckout(product) {
      this.checkoutLoading = product.id
      try {
        const url = await this.$store.dispatch("startCheckout", {
          productId: product.id,
          interval: this.interval,
        })
        this.navigate(url)
      } finally {
        this.checkoutLoading = null
      }
    },
    async openPortal() {
      this.portalLoading = true
      try {
        const url = await this.$store.dispatch("openBillingPortal")
        this.navigate(url)
      } finally {
        this.portalLoading = false
      }
    },
    async confirmCancel() {
      this.cancelLoading = true
      try {
        await this.$store.dispatch("cancelSubscription")
        this.confirming = null
      } finally {
        this.cancelLoading = false
      }
    },
    async reactivate() {
      this.reactivateLoading = true
      try {
        await this.$store.dispatch("reactivateSubscription")
      } finally {
        this.reactivateLoading = false
      }
    },
  },
}
</script>

<style scoped>
.subscription-info {
  max-width: 900px;
}

/* Status banners */
.status-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  font-size: 0.9rem;
}
.status-banner.warning { background: rgba(221, 122, 2, 0.12); color: #9a5000; border: 1px solid rgba(221, 122, 2, 0.3); }
.status-banner.error   { background: rgba(213, 0, 0, 0.08);  color: #c00;    border: 1px solid rgba(213, 0, 0, 0.25); }
.link-btn {
  background: none; border: none; cursor: pointer;
  text-decoration: underline; font-size: inherit; color: inherit; padding: 0; margin-left: 4px;
}

/* Billing toggle */
.billing-toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 28px;
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(0,0,0,0.5);
  user-select: none;
}
.billing-toggle span { cursor: pointer; transition: color 0.2s; }
.billing-toggle span.active { color: rgba(0,0,0,0.85); }
.toggle-track {
  width: 40px; height: 22px;
  background: rgba(0,0,0,0.15);
  border-radius: 11px;
  position: relative;
  cursor: pointer;
  transition: background 0.2s;
}
.toggle-thumb {
  position: absolute; top: 3px; left: 3px;
  width: 16px; height: 16px;
  background: white; border-radius: 50%;
  box-shadow: 0 1px 4px rgba(0,0,0,0.3);
  transition: left 0.2s;
}
.toggle-thumb.right { left: 21px; }
.save-badge {
  display: inline-block;
  background: rgba(0, 150, 80, 0.12);
  color: #006633;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 20px;
  margin-left: 6px;
  vertical-align: middle;
}

/* Products grid */
.products-grid {
  display: flex;
  gap: 16px;
  align-items: stretch;
  flex-wrap: wrap;
}

.product-card {
  flex: 1;
  min-width: 0;
  max-width: 280px;
  border-radius: 10px;
  padding: 24px 20px 20px;
  background: white;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  position: relative;
  border: 2px solid transparent;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.product-card.is-active {
  border-color: rgb(56, 142, 60);
  box-shadow: 0 2px 16px rgba(56,142,60,0.15);
}
.product-card.is-popular {
  border-color: rgb(26, 90, 139);
  box-shadow: 0 2px 16px rgba(26,90,139,0.15);
}

.active-badge, .popular-badge {
  position: absolute; top: -1px; right: 16px;
  padding: 3px 10px;
  border-radius: 0 0 6px 6px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
.active-badge  { background: rgb(56, 142, 60); color: white; }
.popular-badge { background: rgb(26, 90, 139); color: white; }

.product-name {
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 12px;
  color: rgba(0,0,0,0.85);
}

.product-price {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 12px;
}
.product-price.unavailable { color: rgba(0,0,0,0.3); font-size: 1.2rem; }
.amount { font-size: 2rem; font-weight: 800; color: rgba(0,0,0,0.85); line-height: 1; }
.currency { font-size: 0.9rem; font-weight: 600; color: rgba(0,0,0,0.5); }
.period { font-size: 0.85rem; color: rgba(0,0,0,0.4); }

.product-description {
  font-size: 0.82rem;
  color: rgba(0,0,0,0.5);
  margin-bottom: 16px;
  line-height: 1.5;
}

.feature-list {
  list-style: none;
  padding: 0; margin: 0 0 20px;
  flex: 1;
}
.feature-list li {
  font-size: 0.85rem;
  color: rgba(0,0,0,0.7);
  padding: 4px 0;
  padding-left: 20px;
  position: relative;
}
.feature-list li::before {
  content: "✓";
  position: absolute; left: 0;
  color: rgb(0, 129, 13);
  font-weight: 700;
}

.card-footer { margin-top: auto; }

.subscription-meta {
  font-size: 0.78rem;
  color: rgba(0,0,0,0.45);
  margin-bottom: 8px;
}
.current-plan-label {
  font-size: 0.82rem;
  color: rgba(0,0,0,0.4);
  text-align: center;
  padding: 8px 0;
}

.btn-primary, .btn-secondary {
  width: 100%;
  padding: 10px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.2s, transform 0.15s;
}
.btn-primary:hover:not(:disabled), .btn-secondary:hover:not(:disabled) {
  opacity: 0.88; transform: translateY(-1px);
}
.btn-primary:disabled, .btn-secondary:disabled { opacity: 0.5; cursor: default; }

.btn-primary {
  background: rgb(26, 90, 139);
  color: white;
}
.btn-primary.btn-popular {
  background: linear-gradient(135deg, rgb(26, 90, 139) 0%, rgb(16, 60, 100) 100%);
  box-shadow: 0 3px 12px rgba(26, 90, 139, 0.35);
}
.btn-secondary {
  background: rgba(0,0,0,0.06);
  color: rgba(0,0,0,0.7);
  border: 1px solid rgba(0,0,0,0.12);
}

.cancel-confirm {
  background: rgba(200, 0, 0, 0.05);
  border: 1px solid rgba(200, 0, 0, 0.15);
  border-radius: 6px;
  padding: 10px 12px;
}
.cancel-confirm p {
  font-size: 0.8rem;
  color: rgba(0,0,0,0.65);
  margin: 0 0 8px;
  line-height: 1.4;
}
.confirm-actions {
  display: flex;
  gap: 8px;
}
.btn-danger-sm, .btn-ghost-sm {
  flex: 1;
  padding: 6px 8px;
  border-radius: 5px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.2s;
}
.btn-danger-sm:disabled { opacity: 0.5; cursor: default; }
.btn-danger-sm {
  background: rgb(185, 28, 28);
  color: white;
}
.btn-ghost-sm {
  background: rgba(0,0,0,0.06);
  color: rgba(0,0,0,0.6);
  border: 1px solid rgba(0,0,0,0.1);
}

@media (max-width: 600px) {
  .products-grid { flex-direction: column; }
  .product-card { max-width: 100%; }
}
</style>
